/**
 * Risk Engine — kill switch, circuit breaker and configurable limits.
 *
 * Polls GET /risk/status every 10s (unchanged). Limit edits are staged locally
 * and written with PUT /risk/limits.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Power,
  RefreshCw,
  Save,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { api, getErrorMessage } from '@/config/api';
import { formatINR, formatQty, signClass } from '@/lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  StatCard,
  Switch,
} from '@/components/ui';

interface RiskStatus {
  user_id: string;
  kill_switch_active: boolean;
  circuit_breaker_active: boolean;
  daily_stats: {
    realized_pnl: string;
    orders_placed: number;
  };
  limits: Record<string, unknown>;
  risk_rules_count: number;
}

interface LimitField {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'list';
  unit?: string;
  description: string;
}

const LIMIT_FIELDS: LimitField[] = [
  { key: 'max_daily_loss', label: 'Max daily loss', type: 'number', unit: '₹', description: 'Maximum loss before trading halts' },
  { key: 'max_position_size', label: 'Max position size', type: 'number', unit: 'shares', description: 'Maximum shares per single order' },
  { key: 'max_exposure', label: 'Max exposure', type: 'number', unit: '₹', description: 'Maximum total portfolio notional exposure' },
  { key: 'max_orders_per_day', label: 'Max orders per day', type: 'number', description: 'Maximum orders that can be placed in a day' },
  { key: 'max_loss_per_trade', label: 'Max loss per trade', type: 'number', unit: '₹', description: 'Maximum potential loss per individual trade' },
  { key: 'max_price_deviation_pct', label: 'Max price deviation', type: 'number', unit: '%', description: 'Reject orders priced more than X% from LTP (fat-finger protection)' },
  { key: 'require_stop_loss_mis', label: 'Require SL for MIS', type: 'boolean', description: 'Warn on MIS orders without stop-loss protection' },
  { key: 'circuit_breaker_loss_pct', label: 'Circuit breaker loss', type: 'number', unit: '%', description: 'Trip the breaker when daily loss exceeds X% of capital' },
  { key: 'circuit_breaker_cooldown_sec', label: 'Breaker cooldown', type: 'number', unit: 'sec', description: 'Cooldown period after the breaker trips' },
  { key: 'circuit_breaker_consecutive_losses', label: 'Consecutive losses', type: 'number', description: 'Trip the breaker after N consecutive losing trades' },
];

export default function RiskEnginePage() {
  const [riskStatus, setRiskStatus] = useState<RiskStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editedLimits, setEditedLimits] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmKill, setConfirmKill] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/risk/status');
      setRiskStatus(data);
      setEditedLimits(data.limits);
      setLoadError(null);
    } catch (err) {
      setLoadError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10_000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const applyKillSwitch = async (activate: boolean) => {
    setIsToggling(true);
    setMsg(null);
    try {
      if (activate) {
        await api.post('/risk/kill-switch/activate', { reason: 'Manual activation from UI' });
        setMsg({ ok: false, text: 'Kill switch activated — all trading halted' });
      } else {
        await api.post('/risk/kill-switch/deactivate');
        setMsg({ ok: true, text: 'Kill switch deactivated — trading resumed' });
      }
      await fetchStatus();
    } catch (err) {
      setMsg({ ok: false, text: getErrorMessage(err) });
    } finally {
      setIsToggling(false);
      setConfirmKill(false);
    }
  };

  const toggleKillSwitch = () => {
    if (!riskStatus) return;
    if (riskStatus.kill_switch_active) {
      applyKillSwitch(false);
    } else {
      setConfirmKill(true);
    }
  };

  const resetCircuitBreaker = async () => {
    setMsg(null);
    try {
      await api.post('/risk/circuit-breaker/reset');
      setMsg({ ok: true, text: 'Circuit breaker reset — trading resumed' });
      await fetchStatus();
    } catch (err) {
      setMsg({ ok: false, text: getErrorMessage(err) });
    }
  };

  const saveLimits = async () => {
    setIsSaving(true);
    setMsg(null);
    try {
      await api.put('/risk/limits', editedLimits);
      setMsg({ ok: true, text: 'Risk limits updated' });
      await fetchStatus();
    } catch (err) {
      setMsg({ ok: false, text: getErrorMessage(err) });
    } finally {
      setIsSaving(false);
    }
  };

  const killActive = riskStatus?.kill_switch_active ?? false;
  const breakerActive = riskStatus?.circuit_breaker_active ?? false;
  const halted = killActive || breakerActive;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Shield}
        title="Risk engine"
        description="Pre-trade controls applied to every order before it reaches the broker"
        meta={
          <Badge
            tone={halted ? 'danger' : 'success'}
            icon={halted ? ShieldAlert : ShieldCheck}
          >
            {halted ? 'Trading halted' : 'Trading permitted'}
          </Badge>
        }
        actions={
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={fetchStatus}>
            Refresh
          </Button>
        }
      />

      {msg && (
        <Alert tone={msg.ok ? 'success' : 'danger'} onDismiss={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {loadError && (
        <Alert
          tone="danger"
          title="Couldn't load risk status"
          action={
            <Button variant="secondary" size="sm" onClick={fetchStatus}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {/* Controls */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className={killActive ? 'border-danger/40' : undefined}>
          <CardHeader
            title="Kill switch"
            description="Blocks every new order immediately"
            actions={<Badge tone={killActive ? 'danger' : 'neutral'}>{killActive ? 'Active' : 'Inactive'}</Badge>}
          />
          <CardBody className="flex items-center justify-between gap-4">
            <p className="text-sm text-fg-subtle">
              {killActive
                ? 'All order placement is currently blocked for your account.'
                : 'Orders flow through the rule chain as normal.'}
            </p>
            <Button
              variant={killActive ? 'success' : 'danger'}
              icon={Power}
              loading={isToggling}
              disabled={isLoading}
              onClick={toggleKillSwitch}
            >
              {killActive ? 'Deactivate' : 'Activate'}
            </Button>
          </CardBody>
        </Card>

        <Card className={breakerActive ? 'border-warning/40' : undefined}>
          <CardHeader
            title="Circuit breaker"
            description="Trips automatically on loss thresholds"
            actions={
              <Badge tone={breakerActive ? 'warning' : 'neutral'}>{breakerActive ? 'Tripped' : 'Armed'}</Badge>
            }
          />
          <CardBody className="flex items-center justify-between gap-4">
            <p className="text-sm text-fg-subtle">
              {breakerActive
                ? 'The breaker has tripped and is in cooldown. Reset to resume trading.'
                : 'No breach detected in the current session.'}
            </p>
            <Button variant="secondary" icon={Zap} disabled={!breakerActive} onClick={resetCircuitBreaker}>
              Reset
            </Button>
          </CardBody>
        </Card>
      </div>

      {/* Today */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Realized P&L today"
          value={
            <span className={signClass(riskStatus?.daily_stats?.realized_pnl)}>
              {formatINR(riskStatus?.daily_stats?.realized_pnl)}
            </span>
          }
          loading={isLoading}
          icon={BarChart3}
        />
        <StatCard
          label="Orders placed today"
          value={formatQty(riskStatus?.daily_stats?.orders_placed, '0')}
          loading={isLoading}
        />
        <StatCard
          label="Active rules"
          value={formatQty(riskStatus?.risk_rules_count, '0')}
          loading={isLoading}
          icon={Shield}
          hint="Evaluated per order"
        />
      </div>

      {/* Limits */}
      <Card>
        <CardHeader
          title="Risk limits"
          description="Applied by the rule chain on every order"
          actions={
            <Button variant="primary" size="sm" icon={Save} loading={isSaving} onClick={saveLimits}>
              Save limits
            </Button>
          }
        />
        <CardBody>
          {isLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {LIMIT_FIELDS.map((limit) => {
                const value = editedLimits[limit.key];

                if (limit.type === 'boolean') {
                  return (
                    <div key={limit.key} className="flex flex-col gap-1.5">
                      <Switch
                        checked={Boolean(value)}
                        onChange={(checked) =>
                          setEditedLimits((prev) => ({ ...prev, [limit.key]: checked }))
                        }
                        label={limit.label}
                      />
                      <p className="text-xs text-fg-faint">{limit.description}</p>
                    </div>
                  );
                }

                return (
                  <Field key={limit.key} label={limit.label} hint={limit.description}>
                    {(field) => (
                      <Input
                        {...field}
                        type="number"
                        mono
                        value={value === undefined || value === null ? '' : String(value)}
                        onChange={(e) =>
                          setEditedLimits((prev) => ({
                            ...prev,
                            [limit.key]: e.target.value === '' ? '' : Number(e.target.value),
                          }))
                        }
                        suffix={
                          limit.unit ? (
                            <span className="pr-1 text-xs text-fg-faint">{limit.unit}</span>
                          ) : undefined
                        }
                      />
                    )}
                  </Field>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Kill switch confirmation */}
      <Modal
        open={confirmKill}
        onClose={() => setConfirmKill(false)}
        title="Activate the kill switch?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmKill(false)}>
              Cancel
            </Button>
            <Button variant="danger" icon={AlertTriangle} loading={isToggling} onClick={() => applyKillSwitch(true)}>
              Halt all trading
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg-muted">
          Every new order will be blocked until you deactivate it. Existing broker-side orders are not cancelled.
        </p>
      </Modal>
    </div>
  );
}
